import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError, finalize, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { AlertService } from '@cde-fe-app/app/cde-toast/services/alert.service';
import { TOASTR_ALERT_OPTIONS } from '@cde-fe-app/app/cde-toast/utils/toastr-alert-options';
import { ToasterClass, ToasterPosition, ToasterType } from '@cde-fe-app/app/cde-toast/utils/toastr-options';
import { AlarmSeverityCount } from '@cde-fe-app/app/shared/models/alarm.model';
import { MachinesImageUrl } from '@cde-fe-app/app/shared/models/machine.image.model';
import {
  IMachineArchiveDeleteRequest,
  IMachineBasePatch,
  IMachinePatchResponse,
  IOtherMachineProduct,
  IUpdateAllHiddenRequest,
  MachineBase,
} from '@cde-fe-app/app/shared/models/machine.model';
import {
  IMachineOnboarding,
  IOnboardClaasMachineError,
  IOnboardClaasMachineRequest,
  IOnboardClaasMachineResponse,
} from '@cde-fe-app/app/shared/models/machine.onboarding.model';
import { MachineMarker } from '@cde-fe-app/app/shared/models/machine.telemetry.model';
import { IMachineListItem } from '@cde-fe-app/app/shared/models/measurements.model';
import { ServiceCallResponse } from '@cde-fe-app/app/shared/models/service-call.model';
import { AlarmService } from '@cde-fe-app/app/shared/services/alarm.service';
import { MachinesService } from '@cde-fe-app/app/shared/services/machines.service';
import { ProductService } from '@cde-fe-app/app/shared/services/product.service';
import * as RootState from '..';
import * as MachineOverviewActions from './machines.actions';
import { MachineImagesUrl } from './machines.actions';

@Injectable()
export class MachinesOverviewEffects {
  readonly store = inject<Store<RootState.IState>>(Store<RootState.IState>);
  readonly actions$ = inject(Actions);
  readonly machinesService = inject(MachinesService);
  readonly alarmService = inject(AlarmService);
  readonly alertService = inject(AlertService);
  readonly translate = inject(TranslateService);
  readonly router = inject(Router);
  readonly productService = inject(ProductService);
  alertOptions = {
    ...TOASTR_ALERT_OPTIONS,
    timeOut: 3000,
  };

  equipmentList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineOverviewActions.EQUIPMENT_LIST),
      switchMap((data: { payload: string; type: string; machineId: string }) =>
        this.machinesService.equipmentList().pipe(
          map((machines: MachineBase[]) => new MachineOverviewActions.EquipmentListSuccess(machines, data.machineId)),
          catchError(error => of(new MachineOverviewActions.EquipmentListFailure(error))),
          finalize(() => {
            this.store.dispatch(new MachineOverviewActions.EquipmentListComplete());
          })
        )
      )
    )
  );

  updateHidden$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineOverviewActions.UPDATE_HIDDEN),
      map((action: MachineOverviewActions.UpdateHidden) => action.payload),
      switchMap((payload: IMachineBasePatch) =>
        this.machinesService.patchMachine(payload).pipe(
          map((response: IMachinePatchResponse) => new MachineOverviewActions.UpdateHiddenSuccess(response)),
          catchError(error => of(new MachineOverviewActions.UpdateHiddenFailure(error))),
          finalize(() => {
            this.store.dispatch(new MachineOverviewActions.UpdateHiddenComplete());
          })
        )
      )
    )
  );

  updateAllHidden$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineOverviewActions.UPDATE_ALL_HIDDEN),
      map((action: MachineOverviewActions.UpdateAllHidden) => action.payload),
      switchMap((payload: IUpdateAllHiddenRequest) =>
        this.machinesService.patchAllHiddenMachine(payload).pipe(
          map((response: IMachinePatchResponse[]) => new MachineOverviewActions.UpdateAllHiddenSuccess(response)),
          catchError(error => of(new MachineOverviewActions.UpdateAllHiddenFailure(error))),
          finalize(() => {
            this.store.dispatch(new MachineOverviewActions.UpdateAllHiddenComplete());
          })
        )
      )
    )
  );

  archiveMachine$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineOverviewActions.ARCHIVE_MACHINE),
      map((action: MachineOverviewActions.ArchiveMachine) => action.payload),
      switchMap((payload: IMachineArchiveDeleteRequest) =>
        this.machinesService.archiveMachineById(payload).pipe(
          take(1),
          map((machine: MachineBase) => {
            this.alertService.callSnackbarSuccess(
              this.translate.instant('machine_overview.archive_machine_success'),
              '',
              ToasterType.snackbarSuccess, // custom attribute
              {}
            );
            return new MachineOverviewActions.ArchiveMachineSuccess(machine);
          }),
          catchError(error => {
            this.alertService.showError(
              this.translate.instant('machine_overview.archive_machine_failure'),
              this.translate.instant('alert_message.try_again'),
              'alert-error-icon',
              this.alertOptions
            );
            return of(new MachineOverviewActions.ArchiveMachineFailure(error));
          }),
          finalize(() => {
            this.store.dispatch(new MachineOverviewActions.ArchiveMachineComplete());
          })
        )
      )
    )
  );

  removeMachine$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineOverviewActions.REMOVE_MACHINE),
      map((action: MachineOverviewActions.RemoveMachine) => action.payload),
      switchMap((payload: IMachineArchiveDeleteRequest) =>
        this.machinesService.removeMachineById(payload).pipe(
          take(1),
          map(() => {
            this.alertService.callSnackbarSuccess(
              this.translate.instant('machine_overview.delete_machine_success'),
              '',
              ToasterType.snackbarSuccess, // custom attribute
              {}
            );
            return new MachineOverviewActions.RemoveMachineSuccess({
              machineId: payload.machineId,
            });
          }),
          catchError(error => {
            this.alertService.showError(
              this.translate.instant('machine_overview.delete_machine_failure'),
              this.translate.instant('alert_message.try_again'),
              'alert-error-icon',
              this.alertOptions
            );
            return of(new MachineOverviewActions.RemoveMachineFailure(error));
          }),
          finalize(() => {
            this.store.dispatch(new MachineOverviewActions.RemoveMachineComplete());
          })
        )
      )
    )
  );

  addOtherMachine$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineOverviewActions.ADD_OTHER_MACHINE),
      map((action: MachineOverviewActions.AddOtherMachine) => action.payload),
      switchMap((payload: { req: IOtherMachineProduct; type: string }) => {
        return this.productService.addOtherMachineOnboarding(payload.req, payload.type).pipe(
          switchMap((onboardedMachine: IMachineOnboarding) =>
            this.machinesService.getEquipmentById(onboardedMachine.id, onboardedMachine.type).pipe(
              map((response: MachineBase) => {
                this.alertService.callSnackbarSuccess(
                  this.translate.instant('add_products.add_machine_success'),
                  '',
                  ToasterType.snackbarSuccess, // custom attribute
                  {}
                );
                void this.router.navigate(['machines', 'details', response.id]);
                return new MachineOverviewActions.AddOtherMachineSuccess(response);
              })
            )
          ),
          catchError((error: ServiceCallResponse) => {
            if (error?.failCode === 409) {
              this.alertService.showError(
                this.translate.instant('add_products.machine-extensions.errors.equipment-currently-assigned'),
                this.translate.instant('alert_message.try_again'),
                'alert-error-icon',
                this.alertOptions
              );
            } else {
              this.alertService.showError(
                this.translate.instant('add_products.add_machine_failure'),
                this.translate.instant('alert_message.try_again'),
                'alert-error-icon',
                this.alertOptions
              );
            }
            return of(new MachineOverviewActions.AddOtherMachineFailure(error));
          }),
          finalize(() => {
            this.store.dispatch(new MachineOverviewActions.AddOtherMachineComplete());
            this.store.dispatch(new MachineImagesUrl());
          })
        );
      })
    )
  );

  machineMarkers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineOverviewActions.MACHINE_MARKERS),
      switchMap((data: { timeZone: string; language: string }) =>
        this.machinesService.getMachinesMarkers(data.timeZone, data.language).pipe(
          map((markers: MachineMarker[]) => new MachineOverviewActions.MachineMarkersSuccess(markers)),
          catchError(error => of(new MachineOverviewActions.MachineMarkersFailure(error))),
          finalize(() => {
            this.store.dispatch(new MachineOverviewActions.MachineMarkersComplete());
          })
        )
      )
    )
  );

  machineMarkerById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineOverviewActions.MACHINE_MARKER_BY_ID),
      switchMap((data: { uuid: string; timeZone: string; startDate?: string; endDate?: string }) =>
        this.machinesService.getEquipmentMarker(data.uuid, data.timeZone, data.startDate, data.endDate).pipe(
          map((machineMarker: MachineMarker) => {
            if (machineMarker.marker != null) {
              return new MachineOverviewActions.MachineMarkerByIDSuccess(machineMarker);
            } else {
              return new MachineOverviewActions.MachineMarkerByIDFailure('No position for marker available');
            }
          }),
          catchError(error => {
            this.alertService.showError(
              this.translate.instant('alert_message.no_data_for_selected_period'),
              this.translate.instant('alert_message.try_again'),
              'alert-error-icon',
              this.alertOptions
            );
            return of(new MachineOverviewActions.MachineMarkerByIDFailure(error));
          }),
          finalize(() => {
            this.store.dispatch(new MachineOverviewActions.MachineMarkerByIDComplete());
          })
        )
      )
    )
  );

  machineMeasurements$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineOverviewActions.MACHINE_MEASUREMENTS),
      switchMap((data: { payload: string; organizationId: string }) =>
        this.machinesService.equipmentMeasurementsData(data.payload, data.organizationId).pipe(
          map(
            (machineMeasurements: IMachineListItem[]) =>
              new MachineOverviewActions.MachineMeasurementsSuccess(machineMeasurements)
          ),
          catchError(error => of(new MachineOverviewActions.MachineMeasurementsFailure(error))),
          finalize(() => {
            this.store.dispatch(new MachineOverviewActions.MachineMeasurementsComplete());
          })
        )
      )
    )
  );

  machineImagesUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineOverviewActions.MACHINE_IMAGES_URL),
      switchMap(() =>
        this.machinesService.getMachineImagesUrl().pipe(
          map((machineImages: MachinesImageUrl) => new MachineOverviewActions.MachineImagesUrlSuccess(machineImages)),
          catchError(error => of(new MachineOverviewActions.MachineImagesUrlFailure(error))),
          finalize(() => {
            this.store.dispatch(new MachineOverviewActions.MachineImagesUrlComplete());
          })
        )
      )
    )
  );

  machineSeverityCountsAlarms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineOverviewActions.MACHINE_SEVERITY_COUNTS_ALARMS),
      switchMap((data: { timeZone: string; measuredAtLocalDate: string }) =>
        this.alarmService.getMachineSeverityCountsAlarms(data.timeZone, data.measuredAtLocalDate).pipe(
          map(
            (machineAlarmSeverityCount: AlarmSeverityCount[]) =>
              new MachineOverviewActions.MachineSeverityCountsAlarmsSuccess(machineAlarmSeverityCount)
          ),
          catchError(error => of(new MachineOverviewActions.MachineSeverityCountsAlarmsFailure(error))),
          finalize(() => this.store.dispatch(new MachineOverviewActions.MachineSeverityCountsAlarmsComplete()))
        )
      )
    )
  );

  onboardClaasMachineV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineOverviewActions.ONBOARD_CLAAS_MACHINE_V2),
      map((action: MachineOverviewActions.OnboardClaasMachineV2) => action.request),
      mergeMap((request: IOnboardClaasMachineRequest) =>
        this.machinesService.onboardClaasMachineV2(request).pipe(
          switchMap((onboardedMachine: IOnboardClaasMachineResponse) =>
            this.machinesService
              .getEquipmentById(onboardedMachine.id, onboardedMachine.type)
              .pipe(map((machine: MachineBase) => new MachineOverviewActions.OnboardClaasMachineV2Success(machine)))
          ),
          catchError((errorResponse: ServiceCallResponse) => {
            this.handleOnboardingError(errorResponse);
            return of(new MachineOverviewActions.OnboardClaasMachineV2Failure(errorResponse));
          }),
          finalize(() => {
            this.store.dispatch(new MachineOverviewActions.OnboardClaasMachineV2Complete());
          })
        )
      )
    )
  );

  // navigate to details page after success full onboarding. That will trigger
  // requesting /master-data. Going to the machine overview will trigger a list
  // reload. Therefore all data neccessary is loaded after onboarding a new
  // machine
  onboardClaasMachineSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MachineOverviewActions.ONBOARD_CLAAS_MACHINE_V2_SUCCESS),
        map((action: MachineOverviewActions.OnboardClaasMachineV2Success) => action.response),
        tap(response => {
          this.alertService.showInfo(
            this.translate.instant('add_products.add_product_success'),
            '',
            ToasterType.snackbarSuccess,
            {
              // consistent with success effect when adding a foreign machine
              positionClass: ToasterPosition.bottomLeft,
              showToastAction: false,
              type: ToasterType.snackbarSuccess,
              toastClass: ToasterClass.snackbarSuccess,
              closeButton: false,
              disableTimeOut: false,
            }
          );

          this.store.dispatch(new MachineImagesUrl());
          void this.router.navigate(['machines', 'details', response.id]);
        })
      ),
    { dispatch: false }
  );

  private handleOnboardingError(errorResponse: ServiceCallResponse) {
    const error = errorResponse.data as IOnboardClaasMachineError;
    const localMessageKey = error?.localMessageKey?.toLowerCase() ?? '';
    if (error?.status === 409 && localMessageKey.startsWith('machine-extensions.errors.')) {
      this.alertService.showError(
        this.translate.instant(localMessageKey),
        this.translate.instant('alert_message.try_again'),
        'alert-error-icon',
        this.alertOptions
      );
    } else {
      this.alertService.showError(
        this.translate.instant('add_products.add_machine_failure'),
        this.translate.instant('alert_message.try_again'),
        'alert-error-icon',
        this.alertOptions
      );
    }
  }
}
